import {PogisDocument, Regulation, RegulatoryDocument} from 'types';
import i18n from 'i18n/i18n';
import {TFunction} from 'react-i18next';
import {formatDateTime} from 'i18n/localeDateFormat';
import {
	IHTMLXL,
	renderHeaderXL,
	renderHTMLExcel,
	cleanHTMLExcel,
	createZIPXL,
	renderRichtText,
} from './ExcelExportCommon';
import Excel, {RichText, Table, Worksheet} from 'exceljs';
import {DocumentSourceEnum} from 'features/Regulations/RegulationDetail/RegulationDetail.types';
import {funcMapPogisDocument} from 'features/Archive/PogisDocumentPage';

interface IRegulationDetailPage {
	(entityListItems: any[], pageDetails: any, t: TFunction): Promise<void>;
}

interface IRenderTable {
	(
		arrRegDocs: RegulatoryDocument[],
		sheet: Worksheet,
		table: Table,
		intRowCountTop: number,
		t: TFunction,
	): number;
}

export const handleClickRegulationDetailPage: IRegulationDetailPage = async (
	entityListItems,
	pageDetails,
	t,
) => {
	const arrTables: string[] = [];
	const arrImagesUri: string[] = [];
	const arrImagesName: string[] = [];

	const fontTemplate = {name: 'Porsche Next TT', size: 10};
	const fontHeading = {bold: true, ...fontTemplate};
	const fontText = {bold: false, ...fontTemplate};

	const arrRegDoc = entityListItems as RegulatoryDocument[];
	const regulation = pageDetails as Regulation;
	const arrPOGIS = (regulation.regulatoryDocuments ?? [])
		.flatMap(x => x.pogisDocuments)
		.map(x => funcMapPogisDocument(x as PogisDocument))
		.sort((a, b) => Number(b.pogisId) - Number(a.pogisId));

	const workbook = new Excel.Workbook();
	const arrColWidth: number[] = [50, 100, ...new Array(11).fill(25)];
	const strPageName = t('Documents', tRegulationDetail);
	const sheet = workbook.addWorksheet('TRACE_' + strPageName);

	renderHeaderXL(workbook, sheet, arrColWidth, strPageName, t);

	let intRowCount = 4;
	let row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};
	row.getCell('B').value = renderRichtText(
		t('RegulationNumber', tRegulationList),
		regulation.regulationNumber ?? '',
	);

	row.getCell('C').value = renderRichtText(
		t('Name', tRegulationList),
		regulation.name ?? '',
	);

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		t('ContactPersonLabel', tRegulationDetail),
		regulation.contactPersons.map(a => a.name).join(';\n'),
	);

	const tempDivElement = document.createElement('div');
	const arrHTMLXL: IHTMLXL[] = [];
	const arrHTMLXLClean: IHTMLXL[] = [
		{
			font: fontHeading,
			text: t('ShortSummaryLabel', tRegulationDetail) + '\n',
		},
	];
	tempDivElement.innerHTML = regulation.summary
		? '<div>' + regulation.summary + '</div>'
		: '<div></div>';
	renderHTMLExcel(tempDivElement.childNodes[0].childNodes, arrHTMLXL);
	cleanHTMLExcel(arrHTMLXL, arrHTMLXLClean);
	row.getCell('C').value = {richText: arrHTMLXLClean as RichText[]};

	intRowCount++;
	row = sheet.getRow(intRowCount);
	row.alignment = {wrapText: true, vertical: 'top'};

	row.getCell('B').value = renderRichtText(
		t('MainKeywordsLabel', tRegulationDetail),
		regulation.mainKeywords.map(a => a.name).join('; '),
	);

	row.getCell('C').value = renderRichtText(
		t('KeyWordsLabel', tRegulationDetail),
		regulation.keywords.map(a => a.name).join('; '),
	);

	row.getCell('D').value = renderRichtText(
		t('MarketsLabel', tRegulationDetail),
		regulation.markets.map(a => a.name).join('; '),
	);

	if (regulation.attachments && regulation.attachments.length > 0) {
		intRowCount++;
		row = sheet.getRow(intRowCount);
		row.getCell('B').alignment = {wrapText: true, vertical: 'top'};
		row.getCell('B').value = renderRichtText(
			t('AttachmentsLabel', tRegulationDetail),
			regulation.attachments?.map(attach => attach.attachmentId).join(';\n'),
		);
	}

	intRowCount++;
	intRowCount++;

	row = sheet.getRow(intRowCount);
	row.font = {...fontHeading, color: {argb: 'FFFFFFFF'}};

	const columnsXL = [
		{
			name: t('Version', tRegDocList),
		},
		{
			name: t('Keywords', tRegDocList),
		},
		{
			name: t('AlreadyCommunicated', tPogis),
		},
		{
			name: t('Status', tRegDocList),
		},
		{
			name: t('WorkflowStatus', tRegDocList),
		},
		{
			name: t('ModifiedAt', tRegDocList),
		},
		{
			name: t('DateNewTypes', tRegDocList),
		},
		{
			name: t('DateNewRegistration', tRegDocList),
		},
		{
			name: t('DateEffective', tRegDocList),
		},
		{
			name: t('DateExpiration', tRegDocList),
		},
		{
			name: t('ModelYear', tRegDocList),
		},
		{
			name: t('PhaseIn', tRegDocList),
		},
		{
			name: t('PhaseOut', tRegDocList),
		},
	];

	sheet.addTable({
		name: 'tableRegulatoryDocuments',
		ref: 'B' + String(intRowCount),
		style: {
			showRowStripes: true,
		},
		columns: columnsXL,
		rows: [],
	});

	const table = sheet.getTable('tableRegulatoryDocuments');
	if (arrRegDoc.length === 0) {
		table.addRow([...new Array(columnsXL.length).fill('')]);
	}

	intRowCount++;
	intRowCount = renderTable(arrRegDoc, sheet, table, intRowCount, t);
	table.commit();

	const strZipName =
		strPageName + '_' + atob(regulation.id).split('\n')[1].slice(1);
	await createZIPXL(
		workbook,
		strZipName,
		t,
		arrTables,
		arrImagesUri,
		arrImagesName,
		regulation.attachments!,
	);
};

const renderTable: IRenderTable = (
	arrRegDoc,
	sheet,
	table,
	intRowCountTop,
	t,
) => {
	let intRowCount = intRowCountTop;
	let row = sheet.getRow(intRowCount);
	for (let ii = 0; ii < arrRegDoc.length; ii++) {
		row = sheet.getRow(intRowCount);
		row.alignment = {wrapText: true, vertical: 'top'};
		const strPogisIds =
			arrRegDoc[ii].pogisDocuments !== undefined &&
			arrRegDoc[ii].pogisDocuments.length > 0
				? '\n' +
				  t('pogisDocuments', tPogis) +
				  ':\n' +
				  arrRegDoc[ii].pogisDocuments.map(x => x.pogisId).join(';\n')
				: '';
		const strPogis = arrRegDoc[ii].pogisExists
			? t('yes', tPogis) + strPogisIds
			: '';
		table.addRow([
			arrRegDoc[ii].name,
			arrRegDoc[ii].keywords.map(keyword => keyword.name).join(';\n'),
			strPogis,
			arrRegDoc[ii].status
				? t(arrRegDoc[ii].status, {
						ns: 'common/enums',
						keyPrefix: 'RegulatoryDocumentStatus',
				  })
				: '',
			arrRegDoc[ii].workflow.status && arrRegDoc[ii].workflow.status
				? t(arrRegDoc[ii].workflow.status, {
						ns: 'common/enums',
						keyPrefix: 'WorkflowStatus',
				  })
				: '',
			arrRegDoc[ii].modifiedAt
				? formatDateTime(new Date(arrRegDoc[ii].modifiedAt), i18n)
				: '',
			arrRegDoc[ii].dateNewTypes
				? formatDateTime(new Date(arrRegDoc[ii].dateNewTypes), i18n)
				: '',
			arrRegDoc[ii].dateNewRegistration
				? formatDateTime(new Date(arrRegDoc[ii].dateNewRegistration), i18n)
				: '',
			arrRegDoc[ii].dateEffective
				? formatDateTime(new Date(arrRegDoc[ii].dateEffective), i18n)
				: '',
			arrRegDoc[ii].dateExpiration
				? formatDateTime(new Date(arrRegDoc[ii].dateExpiration), i18n)
				: '',
			arrRegDoc[ii].modelYear ?? '',
			(arrRegDoc[ii].phaseIn ?? [])
				.map(
					PI =>
						(PI.date ? formatDateTime(new Date(PI.date), i18n) : '') +
						' (' +
						(PI.status ?? '') +
						')',
				)
				.join(';\n'),
			(arrRegDoc[ii].phaseOut ?? [])
				.map(
					PI =>
						(PI.date ? formatDateTime(new Date(PI.date), i18n) : '') +
						' (' +
						(PI.status ?? '') +
						')',
				)
				.join(';\n'),
		]);
		intRowCount++;
	}

	return intRowCount;
};

const tRegDocList = {
	ns: 'features/regulatorydocuments',
	keyPrefix: 'RegulatoryDocumentsList',
};

const tRegulationDetail = {
	ns: 'features/regulations',
	keyPrefix: 'RegulationDetail',
};

const tRegulationList = {
	ns: 'features/regulations',
	keyPrefix: 'RegulationsList',
};

const tPogis = {
	ns: 'features/pogisdocuments',
	keyPrefix: 'Reference',
};
